/**
 * @module lib/anthropic
 * @summary AI symbol-code generation via the Anthropic API.
 * @description
 *   // HAZARD: this calls api.anthropic.com DIRECTLY from the browser using the
 *   user's own API key (stored in localStorage), exactly as the original tool
 *   did, via the `anthropic-dangerous-direct-browser-access` header. This
 *   exposes the key to anything running in the page and is for the static-first
 *   build only. The FUTURE backend must move this behind a server-side proxy so
 *   the key never reaches the browser — see docs/BACKEND-CLOUD-SERVICES.md
 *   (§ "AI proxy sketch"). This whole module is the single swap point.
 *
 *   Model id is `claude-opus-4-8` (current Opus tier; same pricing as 4.7,
 *   no request-shape changes).
 * @ported-from JCC PFD Engineering Tool v1.1.html lines 5360-5417.
 */
import { KEYS } from './storage';

export interface GenerateOpts {
  apiKey: string;
  description: string;
  imageFile?: File | null;
  fill: string;
  stroke: string;
  width: string | number;
  height: string | number;
  category: string;
  label: string;
}

/** Persist the API key for this browser (so the field is pre-filled next time). */
export function saveApiKey(key: string): void {
  localStorage.setItem(KEYS.apiKey, key);
}
export function loadApiKey(): string {
  return localStorage.getItem(KEYS.apiKey) || '';
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = (ev) => resolve(String(ev.target?.result).split(',')[1]);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

/**
 * Ask Claude to generate the body of `function(c, s)` canvas drawing code.
 * @returns the generated code string (markdown fences stripped).
 */
export async function generateSymbolCode(opts: GenerateOpts): Promise<string> {
  const systemPrompt =
    'You are an expert at writing HTML5 Canvas 2D drawing code for process flow diagram (PFD) equipment symbols.\n\n' +
    'DRAWING CONVENTIONS (you MUST follow these exactly):\n' +
    '- The function receives two arguments: c (CanvasRenderingContext2D) and s (shape object)\n' +
    '- s has: s.x, s.y, s.w, s.h, s.fillColor, s.strokeColor, s.strokeWidth, s.fontSize, s.label, s.tag, s.labelPos\n' +
    '- ALL coordinates MUST be relative to s.x, s.y, s.w, s.h — NEVER hardcoded pixels\n' +
    '- Save/restore style: const og=c.fillStyle; … c.fillStyle=og;\n' +
    '- End every path with c.fill() and c.stroke()\n' +
    "- Optional label: if(s.labelPos==='bottom'){c.fillStyle=s.strokeColor;c.font=s.fontSize+'px sans-serif';c.textAlign='center';c.fillText(s.label||s.tag,s.x+s.w/2,s.y+s.h+s.fontSize+2);}\n" +
    '- Code is the BODY of function(c,s) — no wrapper\n' +
    '- Clean ISO/ISA engineering schematic style. Under 60 lines.\n' +
    `Category: ${opts.category}\nLabel: ${opts.label}\nFill: ${opts.fill}\nStroke: ${opts.stroke}\nWidth: ${opts.width}px, Height: ${opts.height}px\n\n` +
    'Respond with ONLY raw JavaScript (no markdown fences, no explanation).';

  const userContent: unknown[] = [];
  if (opts.imageFile) {
    const b64 = await fileToBase64(opts.imageFile);
    userContent.push({
      type: 'image',
      source: { type: 'base64', media_type: opts.imageFile.type, data: b64 },
    });
  }
  userContent.push({
    type: 'text',
    text: opts.description || 'Generate canvas drawing code for this equipment symbol.',
  });

  const resp = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': opts.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: 'claude-opus-4-8',
      max_tokens: 1500,
      system: systemPrompt,
      messages: [{ role: 'user', content: userContent }],
    }),
  });
  if (!resp.ok) {
    const eb = await resp.text();
    throw new Error('API ' + resp.status + ': ' + eb);
  }
  const data = await resp.json();
  // The text block isn't guaranteed to be first — find it.
  const blocks: Array<{ type?: string; text?: string }> = data?.content || [];
  const raw: string = blocks.find((b) => b.type === 'text')?.text || '';
  return raw
    .replace(/^```[\w]*\n?/, '')
    .replace(/\n?```$/, '')
    .trim();
}
